import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ProfileInformationTab from './components/ProfileInformationTab';
import PrivacyControlsTab from './components/PrivacyControlsTab';
import PhotoManagementSection from './components/PhotoManagementSection';
import AccountSecurityTab from './components/AccountSecurityTab';
import NotificationPreferencesTab from './components/NotificationPreferencesTab';
import SubscriptionManagementSection from './components/SubscriptionManagementSection';
import { useAuth } from '../../contexts/AuthContext';
import { useError } from '../../contexts/ErrorContext';
import { profileService } from '../../services/supabaseService';
import { useEffect } from 'react';

const UserProfileSettings = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading, refreshProfile } = useAuth();
  const { showError } = useError();
  const [activeTab, setActiveTab] = useState('profile');
  const [userGender, setUserGender] = useState('male');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [profileData, setProfileData] = useState({
    displayName: '',
    age: 25,
    city: '',
    relationshipStatus: 'single',
    bio: '',
    interests: []
  });

  const [privacySettings, setPrivacySettings] = useState({
    privacyLevel: 'high',
    photoBlur: true,
    blurIntensity: 'high',
    hideFace: false,
    profileVisibility: 'anonymous',
    messageAccess: 'verified-only',
    showOnlineStatus: false,
    showLastActive: false,
    allowProfileViews: true
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    newMessages: true,
    newMatches: true,
    profileViews: true,
    subscriptionReminders: true,
    promotionalEmails: false,
    weeklyDigest: true,
    emailFrequency: 'instant',
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00'
  });

  const [photos, setPhotos] = useState([]);

  const [subscriptionData, setSubscriptionData] = useState({
    plan: 'Free',
    status: 'inactive',
    amount: 0,
    currency: '₹'
  });

  // Helper to safely extract profile data (userProfile is now always in camelCase from AuthContext)
  const getProfileValue = (profile, key, defaultValue = '') => {
    if (!profile) return defaultValue;
    return profile[key] !== undefined && profile[key] !== null ? profile[key] : defaultValue;
  };

  // Auth check is handled by ProtectedRoute
  useEffect(() => {
    if (userProfile && !loading) {
      loadProfileData();
    }
  }, [userProfile, loading]);

  // Reload photos when userProfile changes (e.g., after refresh or navigation)
  useEffect(() => {
    if (userProfile && !loading) {
      if (userProfile?.photos && Array.isArray(userProfile.photos) && userProfile.photos.length > 0) {
        const loadedPhotos = userProfile.photos.map((url, index) => ({
          id: Date.now() + index, // Use timestamp for unique IDs
          url,
          alt: userProfile?.photosAlt?.[index] || 'User photo',
          privacyLevel: getProfileValue(userProfile, 'privacyLevel', 'medium'),
          isPrimary: url === userProfile?.primaryPhoto
        }));
        console.log('Loading photos from userProfile:', loadedPhotos.length);
        setPhotos(loadedPhotos);
      } else {
        console.log('No photos in userProfile, setting empty array');
        setPhotos([]);
      }
    }
  }, [userProfile?.photos, userProfile?.photosAlt, userProfile?.primaryPhoto, userProfile?.id, loading]);

  const loadProfileData = () => {
    if (!userProfile) {
      console.warn('loadProfileData called but userProfile is null');
      return;
    }
    
    // userProfile is now always in camelCase from AuthContext
    setUserGender(getProfileValue(userProfile, 'gender', 'male'));
    
    setProfileData({
      displayName: getProfileValue(userProfile, 'displayName', ''),
      age: getProfileValue(userProfile, 'age', 25),
      city: getProfileValue(userProfile, 'city', ''),
      relationshipStatus: getProfileValue(userProfile, 'relationshipStatus', 'single'),
      bio: getProfileValue(userProfile, 'bio', ''),
      interests: Array.isArray(userProfile?.interests) ? userProfile.interests : []
    });

    setPrivacySettings({
      privacyLevel: getProfileValue(userProfile, 'privacyLevel', 'medium'),
      photoBlur: userProfile?.photoBlurred !== false,
      blurIntensity: getProfileValue(userProfile, 'blurIntensity', 'medium'),
      hideFace: userProfile?.hideFace || false,
      profileVisibility: getProfileValue(userProfile, 'profileVisibility', 'limited'),
      messageAccess: getProfileValue(userProfile, 'messageAccess', 'verified'),
      showOnlineStatus: userProfile?.showOnlineStatus !== false,
      showLastActive: userProfile?.showLastActive || false,
      allowProfileViews: userProfile?.appearInSearch !== false
    });

    if (userProfile?.photos && Array.isArray(userProfile.photos) && userProfile.photos.length > 0) {
      setPhotos(userProfile.photos.map((url, index) => ({
        id: index + 1,
        url,
        alt: userProfile?.photosAlt?.[index] || 'User photo',
        privacyLevel: getProfileValue(userProfile, 'privacyLevel', 'medium'),
        isPrimary: url === userProfile?.primaryPhoto
      })));
    } else {
      setPhotos([]);
    }

    setSubscriptionData({
      plan: getProfileValue(userProfile, 'subscriptionPlan', 'Free'),
      status: getProfileValue(userProfile, 'subscriptionStatus', 'inactive'),
      amount: (userProfile?.subscriptionStatus === 'active' || userProfile?.subscriptionStatus === 'premium' || userProfile?.subscriptionStatus === 'trialing') ? 2500 : 0,
      currency: '₹',
      startDate: userProfile?.subscriptionStartDate || null,
      renewalDate: userProfile?.subscriptionRenewalDate || null,
      autoRenew: true,
      paymentMethod: 'UPI'
    });
  };

  const tabs = [
  { id: 'profile', label: 'Profile', icon: 'User' },
  { id: 'privacy', label: 'Privacy', icon: 'Shield' },
  { id: 'photos', label: 'Photos', icon: 'Image' },
  { id: 'security', label: 'Security', icon: 'Lock' },
  { id: 'notifications', label: 'Notifications', icon: 'Bell' },
  ...(userGender === 'male' ? [{ id: 'subscription', label: 'Subscription', icon: 'Crown' }] : [])];


  const handleSaveProfile = async () => {
    if (!user) {
      alert('You must be logged in to save your profile.');
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // Prepare updates with proper data types and validation
      const updates = {
        displayName: profileData?.displayName?.trim() || '',
        age: parseInt(profileData?.age) || 25,
        city: profileData?.city?.trim() || '',
        relationshipStatus: profileData?.relationshipStatus || 'single',
        bio: profileData?.bio?.trim() || '',
        interests: Array.isArray(profileData?.interests) ? profileData?.interests : [],
        // Privacy settings
        privacyLevel: privacySettings?.privacyLevel || 'medium',
        photoBlurred: privacySettings?.photoBlur !== false,
        blurIntensity: privacySettings?.blurIntensity || 'medium',
        hideFace: privacySettings?.hideFace || false,
        profileVisibility: privacySettings?.profileVisibility || 'limited',
        messageAccess: privacySettings?.messageAccess || 'verified',
        showOnlineStatus: privacySettings?.showOnlineStatus !== false,
        showLastActive: privacySettings?.showLastActive || false,
        appearInSearch: privacySettings?.allowProfileViews !== false,
        // Photos - only include if there are actual photos
        ...(Array.isArray(photos) && photos.length > 0 ? {
          photos: photos.map(p => p?.url || p).filter(Boolean),
          photosAlt: photos.map(p => p?.alt || 'User photo'),
          primaryPhoto: photos.find(p => p?.isPrimary)?.url || photos[0]?.url || null,
          primaryPhotoAlt: photos.find(p => p?.isPrimary)?.alt || photos[0]?.alt || 'User profile photo'
        } : {})
      };

      // Validate required fields
      if (!updates.displayName || updates.displayName.length < 3) {
        alert('Display name must be at least 3 characters.');
        setIsSaving(false);
        return;
      }

      if (!updates.age || updates.age < 18 || updates.age > 100) {
        alert('Age must be between 18 and 100.');
        setIsSaving(false);
        return;
      }

      if (!updates.city || updates.city.trim().length === 0) {
        alert('City is required.');
        setIsSaving(false);
        return;
      }

      if (!updates.relationshipStatus) {
        alert('Relationship status is required.');
        setIsSaving(false);
        return;
      }

      const result = await profileService?.updateProfile(user?.id, updates);
      
      if (result) {
        // Result is already in camelCase from toCamelCase conversion
        // Update local state with the saved data
        setProfileData({
          displayName: result?.displayName || profileData?.displayName || '',
          age: result?.age || profileData?.age || 25,
          city: result?.city || profileData?.city || '',
          relationshipStatus: result?.relationshipStatus || profileData?.relationshipStatus || 'single',
          bio: result?.bio || profileData?.bio || '',
          interests: Array.isArray(result?.interests) ? result.interests : (profileData?.interests || [])
        });
        
        // Also update privacy settings if they were included in the update
        if (result?.privacyLevel !== undefined) {
          setPrivacySettings(prev => ({
            ...prev,
            privacyLevel: result.privacyLevel || prev.privacyLevel,
            photoBlur: result.photoBlurred !== false,
            blurIntensity: result.blurIntensity || prev.blurIntensity,
            hideFace: result.hideFace || false,
            profileVisibility: result.profileVisibility || prev.profileVisibility,
            messageAccess: result.messageAccess || prev.messageAccess,
            showOnlineStatus: result.showOnlineStatus !== false,
            showLastActive: result.showLastActive || false,
            allowProfileViews: result.appearInSearch !== false
          }));
        }
        
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        throw new Error('Profile update returned no data');
      }
    } catch (error) {
      console.error('Save profile error:', error);
      
      // Show user-friendly error message using error context if available
      let errorMessage = 'Failed to save profile. Please try again.';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.code === '23505') {
        errorMessage = 'This information conflicts with another profile. Please check your data.';
      } else if (error?.code === '23514') {
        errorMessage = 'Invalid data. Please check all fields and try again.';
      } else if (error?.code === 'PGRST301') {
        errorMessage = 'You don\'t have permission to update this profile.';
      } else if (error?.status === 400) {
        errorMessage = 'Invalid data format. Please check all fields and ensure they match the required format.';
      }
      
      // Use error context if available, fallback to alert
      if (showError) {
        showError(errorMessage);
      } else {
        alert(errorMessage);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePrivacy = (data) => {
    setPrivacySettings(data);
  };

  const handleSaveNotifications = (data) => {
    setNotificationSettings(data);
  };

  const handlePhotoUpdate = async (photos) => {
    // Update local state immediately for better UX
    setPhotos(photos);
    
    // Auto-save photos to profile
    if (!user?.id) {
      console.error('Cannot save photos: user not logged in');
      return;
    }
    
    try {
      const updates = {
        photos: photos.map(p => p?.url || p).filter(Boolean),
        photosAlt: photos.map(p => p?.alt || 'User photo'),
        primaryPhoto: photos.find(p => p?.isPrimary)?.url || photos[0]?.url || null,
        primaryPhotoAlt: photos.find(p => p?.isPrimary)?.alt || photos[0]?.alt || 'User profile photo'
      };

      console.log('Saving photos to profile:', updates);
      const result = await profileService?.updateProfile(user?.id, updates);
      
      if (!result) {
        throw new Error('Profile update returned no data');
      }

      console.log('Photos saved successfully, refreshing profile...');
      
      // Refresh profile in AuthContext to update userProfile
      if (refreshProfile) {
        refreshProfile();
      }
      
      // Also reload photos directly from database to ensure consistency
      try {
        const updatedProfile = await profileService?.getProfile(user?.id);
        console.log('Reloaded profile with photos:', updatedProfile?.photos);
        
        if (updatedProfile?.photos && Array.isArray(updatedProfile.photos) && updatedProfile.photos.length > 0) {
          const reloadedPhotos = updatedProfile.photos.map((url, index) => ({
            id: Date.now() + index, // Use timestamp for unique IDs
            url,
            alt: updatedProfile?.photosAlt?.[index] || 'User photo',
            privacyLevel: getProfileValue(updatedProfile, 'privacyLevel', 'medium'),
            isPrimary: url === updatedProfile?.primaryPhoto,
            storagePath: null // Will be set if needed
          }));
          setPhotos(reloadedPhotos);
          console.log('Photos reloaded from database:', reloadedPhotos.length);
        } else {
          console.warn('No photos found in reloaded profile');
        }
      } catch (reloadError) {
        console.error('Error reloading photos:', reloadError);
        // Don't throw - photos were saved, just couldn't reload
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Save photos error:', error);
      
      // Revert local state on error
      if (userProfile?.photos && Array.isArray(userProfile.photos)) {
        const originalPhotos = userProfile.photos.map((url, index) => ({
          id: index + 1,
          url,
          alt: userProfile?.photosAlt?.[index] || 'User photo',
          privacyLevel: getProfileValue(userProfile, 'privacyLevel', 'medium'),
          isPrimary: url === userProfile?.primaryPhoto
        }));
        setPhotos(originalPhotos);
      }
      
      const errorMessage = error?.message || 'Failed to save photos. Please try again.';
      if (showError) {
        showError(errorMessage);
      } else {
        alert(errorMessage);
      }
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <ProfileInformationTab 
            profileData={profileData} 
            onProfileDataChange={setProfileData}
            onSave={handleSaveProfile} 
          />
        );
      case 'privacy':
        return <PrivacyControlsTab privacySettings={privacySettings} onSave={handleSavePrivacy} />;
      case 'photos':
        return <PhotoManagementSection photos={photos} onPhotoUpdate={handlePhotoUpdate} />;
      case 'security':
        return <AccountSecurityTab onSave={(data) => console.log('Security saved:', data)} />;
      case 'notifications':
        return <NotificationPreferencesTab notificationSettings={notificationSettings} onSave={handleSaveNotifications} />;
      case 'subscription':
        return <SubscriptionManagementSection userGender={userGender} subscriptionData={subscriptionData} />;
      default:
        return null;
    }
  };

  return (
    <>
      <Helmet>
        <title>Profile Settings - Bamchik</title>
        <meta name="description" content="Manage your profile, privacy settings, and account preferences on Bamchik discreet dating platform" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="mx-auto max-w-[1200px] px-4 py-6 lg:px-6 lg:py-8">
          <div className="mb-6 lg:mb-8">
            <button
              onClick={() => navigate('/member-dashboard')}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth mb-4">

              <Icon name="ArrowLeft" size={16} />
              Back to Dashboard
            </button>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
              Account Settings
            </h1>
            <p className="text-sm md:text-base text-muted-foreground mt-2">
              Manage your profile, privacy, and account preferences
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <div className="hidden lg:block bg-card rounded-xl shadow-warm p-4 sticky top-20">
                <nav className="space-y-1">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-smooth ${
                    activeTab === tab?.id ?
                    'bg-primary text-primary-foreground' :
                    'text-foreground hover:bg-muted'}`
                    }>

                      <Icon
                      name={tab?.icon}
                      size={20}
                      color={activeTab === tab?.id ? '#FFFFFF' : 'currentColor'} />

                      <span className="font-caption font-medium">{tab?.label}</span>
                    </button>
                  )}
                </nav>
              </div>

              <div className="lg:hidden mb-4">
                <div className="bg-card rounded-xl shadow-warm p-2 overflow-x-auto">
                  <div className="flex gap-2 min-w-max">
                    {tabs?.map((tab) =>
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-smooth ${
                      activeTab === tab?.id ?
                      'bg-primary text-primary-foreground' :
                      'text-foreground hover:bg-muted'}`
                      }>

                        <Icon
                        name={tab?.icon}
                        size={18}
                        color={activeTab === tab?.id ? '#FFFFFF' : 'currentColor'} />

                        <span className="text-sm font-caption font-medium">{tab?.label}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="bg-card rounded-xl shadow-warm p-4 md:p-6 lg:p-8">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-4">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                      <p className="text-sm text-muted-foreground">Loading profile data...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {saveSuccess && (
                      <div className="mb-4 p-4 bg-success/10 border border-success/20 rounded-lg flex items-center gap-2">
                        <Icon name="CheckCircle" size={20} className="text-success" />
                        <p className="text-sm text-success font-medium">Profile saved successfully!</p>
                      </div>
                    )}
                    {renderTabContent()}
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>);

};

export default UserProfileSettings;